import React from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import styles from './styles/CurrentPage.module.scss';

import { OmaLummeApp } from '../../omaLummeContext';

import Box from '../Common/Box';
import Loading from '../Common/Loading';
import Header from './Header';

// Images.
import logo from '../../logo.svg';
import maintenanceImage from '../../images/maintenance.svg';

// Actions.
import { validateToken } from '../../actions';

// Utils.
import { initialUsers } from '../../utils/currentPage';
import { setUserDetails, logUserOut } from '../../utils/login';

type AuthProps = {
  children: React.JSX.Element;
  setRedirectUser: (value: React.SetStateAction<boolean>) => void;
  userHasToken: boolean;
  setUserHasToken: (value: React.SetStateAction<boolean>) => void;
};

export const AuthenticationWrapper = ({ children, setRedirectUser, userHasToken, setUserHasToken }: AuthProps) => {
  const dispatch = OmaLummeApp.useDispatch();
  const { users, maintenance } = OmaLummeApp.useState();
  const { t } = useTranslation();

  const [fetchingUserData, setFetchingUserData] = React.useState(false);

  const [shouldValidateUser, setShouldValidateUser] = React.useState(false);
  const [userTokenTimestamp, setUserTokenTimestamp] = React.useState(0);
  const location = useLocation();
  const { pathname } = location;
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const param = params.get('code');
  const source = params.get('source');

  type OmaLummeLocationState = {
    from: string;
    source: string;
  };

  const locationState = location.state as OmaLummeLocationState;
  const from = location.state ? locationState.from : location.pathname;
  const navigate = useNavigate();

  // We default to an hour.
  let tokenDuration = 3600000;
  // If the REACT_TOKEN_TIMEOUT value is set in .env we use that value instead.
  if (import.meta.env.VITE_TOKEN_TIMEOUT) {
    tokenDuration = parseInt(import.meta.env.VITE_TOKEN_TIMEOUT);
  }

  // Validate the token and log the user out when needed.
  React.useEffect(() => {
    async function validateStoredToken(): Promise<void> {
      if (!fetchingUserData) {
        setFetchingUserData(true);
        const userDetails = await validateToken();

        if (userDetails) {
          if (users === initialUsers) {
            const usersToSet = await setUserDetails(userDetails, users);

            // If the user has a fake uid (0) and has no children then we know they need to do some work on their
            // account so we redirect them to the correct place.
            if (usersToSet.parentUser.userId === 0 && (!usersToSet.childUsers || usersToSet.childUsers?.length === 0)) {
              setRedirectUser(true);
            }
            dispatch(['changeUsers', usersToSet]);
          }
          if (!isNaN(userDetails.customerId)) {
            setUserTokenTimestamp(Date.now());
            setShouldValidateUser(false);
          } else {
            sessionStorage.removeItem('token');
            setUserHasToken(false);
            setUserTokenTimestamp(0);
          }
        } else {
          sessionStorage.removeItem('token');
          setUserHasToken(false);
          setUserTokenTimestamp(0);
        }
        // User has been validated.
        setShouldValidateUser(false);
        setFetchingUserData(false);
      }
    }

    const token = sessionStorage.getItem('token');
    // If the user has a token.
    if (token) {
      if (maintenance?.active) {
        logUserOut();
      } else {
        setUserHasToken(true);

        // and it is older than an hour.
        if (Date.now() - userTokenTimestamp > tokenDuration || users === initialUsers) {
          // Validate the token.
          validateStoredToken();
        }
        if (param) {
          navigate('/', { state: { from: from, source: source } });
        }
      }
    } else {
      if (userHasToken) {
        setUserHasToken(false);
        setUserTokenTimestamp(0);
        dispatch(['changeUsers', initialUsers]);
      }

      const anonUrls = ['article', 'alert', 'embed'];
      const anonBaseUrls = ['customerService'];
      const baseUrl = pathname.split('/')[1];
      const subUrl = pathname.split('/')[2];

      if (baseUrl && !anonUrls.includes(baseUrl) && !(anonBaseUrls.includes(baseUrl) && !subUrl) && !param) {
        navigate('/', { state: { from: from, source: source } });
      }
    }
  }, [users, userTokenTimestamp, shouldValidateUser, param, tokenDuration, maintenance, pathname, userHasToken, location.state, from]);

  // If the token is older than the set duration we should validate it. This should ensure it is fired when the page is changed.
  if (userTokenTimestamp > 0 && Date.now() - userTokenTimestamp > tokenDuration) {
    // Validate the token.
    setShouldValidateUser(true);
  }

  if (maintenance?.active) {
    return (
      <div className={styles.maintenanceWrapper}>
        <Box>
          <img src={logo} className={styles.maintenanceLogo} alt="Lumme Energia" />
          <div>
            <div>
              <h1>{t(`landingPage:login.maintenance`)}</h1>
              <p>{maintenance?.message}</p>
            </div>

            <img src={maintenanceImage} className={styles.maintenanceImage} alt="Lumme Energia" />
          </div>
        </Box>
      </div>
    );
  } else if (fetchingUserData && pathname !== '/') {
    return (
      <div className={`${styles.wrapper} ${styles.grid}`}>
        <Header />
        <div className={styles.fullWidth}>
          <Loading />
        </div>
      </div>
    );
  } else {
    return children;
  }
};
